import { useState } from 'react';
import auth from '@mp/utils/firebase/firebase.web';
import {
  MultiFactorError,
  MultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  PhoneMultiFactorInfo,
  RecaptchaVerifier,
  UserCredential,
  getMultiFactorResolver,
  multiFactor,
} from 'firebase/auth';
import { useProNavigate } from '@mp/contexts';
import { useCurrentUser } from '../useCurrentUser';
import { isRunningCypress } from '@mp/utils/testUtils';

let globalRecaptchaVerifier: RecaptchaVerifier | null = null;

const getGlobalRecaptchaVerifier = async (): Promise<RecaptchaVerifier> => {
  const container = document.getElementById('r-container');
  if (!container) {
    throw new Error('Missing #r-container');
  }

  const needsRecreate =
    !globalRecaptchaVerifier ||
    // @ts-ignore
    globalRecaptchaVerifier.destroyed ||
    // @ts-ignore Firebase wipes iframe but doesn't mark destroyed
    !container.hasChildNodes();

  if (needsRecreate) {
    try {
      // clear old verifier if it exists
      if (globalRecaptchaVerifier) {
        try {
          // @ts-ignore
          globalRecaptchaVerifier.clear?.();
        } catch (e) {
          console.warn('Could not clear old verifier', e);
        }
      }

      globalRecaptchaVerifier = new RecaptchaVerifier('r-container', { size: 'invisible' }, auth);
      await globalRecaptchaVerifier.render();
    } catch (err) {
      console.error('Recaptcha render failed', err);
      throw err;
    }
  }

  return globalRecaptchaVerifier!;
};

export const useMFAEnrollment = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const phoneAuthProvider = new PhoneAuthProvider(auth);

  const { navigate } = useProNavigate();
  const { isAgency, isSaas } = useCurrentUser();

  const startEnrollment = async (phoneNumber: string) => {
    const recaptchaVerifier = await getGlobalRecaptchaVerifier();

    try {
      setLoading(true);
      setError(null);

      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not signed in');
      }

      const session = await multiFactor(user).getSession();

      const phoneInfoOptions = {
        phoneNumber,
        session,
      };

      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        phoneInfoOptions,
        recaptchaVerifier,
      );

      return verificationId;
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err);
      } else {
        setError(new Error('Unexpected error'));
      }
      // Don't clear the singleton - let it be reused
      return null;
    } finally {
      setLoading(false);
    }
  };

  const enrollPhoneMfa = async (verificationCode: string, verificationId: string) => {
    if (!verificationId) {
      throw new Error('No verification ID available');
    }

    try {
      setLoading(true);
      setError(null);

      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not signed in');
      }

      const credential = PhoneAuthProvider.credential(verificationId, verificationCode);
      const assertion = PhoneMultiFactorGenerator.assertion(credential);

      await multiFactor(user).enroll(assertion, 'Phone number');
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err);
        throw err;
      } else {
        const fallback = new Error('Unexpected error');
        setError(fallback);
        throw fallback;
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMfaChallenge = async (
    error: MultiFactorError,
  ): Promise<{
    verificationId: string;
    resolver: MultiFactorResolver;
  } | null> => {
    const recaptchaVerifier = await getGlobalRecaptchaVerifier();
    try {
      setLoading(true);
      setError(null);

      const resolver = getMultiFactorResolver(auth, error);
      const hint = resolver.hints[0];
      const session = resolver.session;

      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        {
          multiFactorHint: hint,
          session: session,
        },
        recaptchaVerifier,
      );

      return { verificationId, resolver };
    } catch (err: unknown) {
      setError(err instanceof Error ? err : new Error('Unexpected error during MFA challenge'));
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getUserPhoneNumberMasked = (error: MultiFactorError): string | undefined => {
    const resolver = getMultiFactorResolver((error as any).auth, error);

    const phoneHint = resolver.hints.find(
      (hint): hint is PhoneMultiFactorInfo => 'phoneNumber' in hint,
    );

    if (phoneHint?.phoneNumber) {
      return `(•••) •••-${phoneHint.phoneNumber.slice(-4)}`;
    }

    return undefined;
  };

  const resolveMfaSignIn = async (
    code: string,
    verificationId: string,
    resolver: MultiFactorResolver,
  ): Promise<UserCredential> => {
    try {
      setLoading(true);
      setError(null);

      const credential = PhoneAuthProvider.credential(verificationId, code);
      const assertion = PhoneMultiFactorGenerator.assertion(credential);

      return await resolver.resolveSignIn(assertion);
    } catch (err: unknown) {
      setError(err instanceof Error ? err : new Error('Unexpected error resolving MFA'));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const redirectToMFAOnSignup = () => {
    const firebaseUser = auth.currentUser;
    const mfaInfo = firebaseUser ? multiFactor(firebaseUser) : null;
    if (
      !isAgency &&
      !isSaas &&
      !isRunningCypress() &&
      !Boolean(window?.localStorage?.getItem('impersonating')) &&
      (!firebaseUser?.emailVerified || !mfaInfo?.enrolledFactors?.length)
    ) {
      if (!firebaseUser?.emailVerified) {
        navigate('MfaIdentityVerificationScreen', {
          forward: JSON.stringify({
            forwardTo: 'Application',
            forwardParams: undefined,
          }) as any,
          flow: 'SIGN_UP',
        });
      } else {
        navigate('MfaSmsCodeEntryScreen', {
          forward: JSON.stringify({
            forwardTo: 'Application',
            forwardParams: undefined,
          }) as any,
          flow: 'SIGN_UP',
        });
      }
    }
  };

  return {
    startEnrollment,
    enrollPhoneMfa,
    handleMfaChallenge,
    resolveMfaSignIn,
    getUserPhoneNumberMasked,
    loading,
    error,
    redirectToMFAOnSignup,
  };
};
